/**
 * THE HELD COPY — a local copy that could not be updated is never treated as
 * current (owner report 2026-09-22 — Miles: his newest message in a group thread
 * "is appearing and disappearing like really bad … like whiplash to the user").
 *
 * When the browser's storage bin is full, localStorage refuses a thread's write
 * and its copy on disk stays FROZEN at the last list that fit. Everything that
 * later reads that key — the reader behind addMessage's base, the queued-turn
 * lane's routed updates, the reconcile poll, the persist guards' own baseline,
 * the skip-unchanged check — used to read that frozen list and act on it as if
 * it were current: a queued reply could not find its own placeholder, and the
 * persist path re-attempted the same refused write (and its 8x-payload reclaim,
 * and its broadcast) on every pass.
 *
 * This module keeps, per key, the JSON this tab last TRIED to store when
 * localStorage refused it. The chokepoints read through it
 * (`utils.getFromLocalStorage`, `utils.localStorageHasKey`,
 * `threadPersist.rehydrateThreadMessages`, the persist guards), so every
 * per-thread read in the app gets this tab's latest copy without being edited
 * one by one. A held copy is released the moment localStorage holds a fresher
 * copy of that key from ANY tab — this tab's own successful write, or a
 * `storage` event naming the key (another tab stored it) — so a held copy can
 * never outlive the frozen state it stands in for.
 *
 * Memory only, per tab, bounded by total characters (oldest-touched first out).
 * It is not a store: a reload starts empty and reads the frozen copy exactly as
 * before, while the server listener and the IndexedDB rehydrate heal it.
 *
 * Imports NOTHING on purpose: storageQuota, utils and threadPersist all call
 * it, and eleven chat-isolation suites mock `../threadPersist` wholesale — a
 * helper living there would be erased by the mock (the 68d73d5 trap: "the
 * constant lives where a mock cannot erase it").
 */

/**
 * The most characters held across all keys. A thread's cache can be large (the
 * one in the 2026-09-22 recording was ~430k characters for its newest 100 rows),
 * and the origin's whole localStorage is ~5M characters in Chromium, so this
 * holds a full bin's worth without ever becoming the memory problem.
 */
export const MAX_HELD_CHARS = 5 * 1024 * 1024;

/** Insertion order = recency (a touch re-inserts), so the first key is the coldest. */
const held = new Map<string, string>();
let heldChars = 0;
let storageListenerInstalled = false;

function dropKey(key: string): void {
  const prev = held.get(key);
  if (prev === undefined) return;
  heldChars -= prev.length;
  held.delete(key);
}

/**
 * Another tab stored (or removed) the key: localStorage now holds a copy fresher
 * than this tab's frozen one, so the held copy has nothing left to stand in for.
 * `key === null` is `localStorage.clear()` from another tab — every copy goes.
 */
function onStorageEvent(event: StorageEvent): void {
  try {
    if (typeof localStorage !== 'undefined' && event.storageArea && event.storageArea !== localStorage) return;
  } catch { /* storage unavailable — fall through and release */ }
  if (event.key === null) {
    held.clear();
    heldChars = 0;
    return;
  }
  dropKey(event.key);
}

function ensureStorageListener(): void {
  if (storageListenerInstalled) return;
  if (typeof window === 'undefined' || typeof window.addEventListener !== 'function') return;
  try {
    window.addEventListener('storage', onStorageEvent);
    storageListenerInstalled = true;
  } catch { /* no listener ⇒ a held copy is released only by this tab's own writes */ }
}

/** localStorage refused this write: hold what this tab tried to store. */
export function holdRefusedWrite(key: string, json: string): void {
  if (typeof key !== 'string' || !key || typeof json !== 'string') return;
  ensureStorageListener();
  dropKey(key);
  held.set(key, json);
  heldChars += json.length;
  // Coldest first; the key just written always survives, however large.
  for (const [coldKey] of held) {
    if (heldChars <= MAX_HELD_CHARS) break;
    if (coldKey === key) continue;
    dropKey(coldKey);
  }
}

/** localStorage now holds a current copy of this key (a write landed). */
export function releaseHeldCopy(key: string): void {
  if (held.size === 0) return;
  dropKey(key);
}

/** This tab's latest copy of the key when localStorage could not take it; else null. */
export function readHeldCopy(key: string): string | null {
  if (held.size === 0) return null;
  const value = held.get(key);
  return value === undefined ? null : value;
}

/** Release every held copy whose key the predicate names (purges, sign-out). */
export function releaseHeldCopiesWhere(predicate: (key: string) => boolean): void {
  if (held.size === 0) return;
  for (const key of Array.from(held.keys())) {
    let match = false;
    try { match = predicate(key); } catch { match = false; }
    if (match) dropKey(key);
  }
}

/** Test seam: how many keys are held right now. */
export function heldCopyCount(): number {
  return held.size;
}

/** Test seam: forget everything (module state survives between tests otherwise). */
export function __resetHeldCopiesForTests(): void {
  held.clear();
  heldChars = 0;
}
